import { useQuery } from '@tanstack/react-query';
import {
  fetchUserSubmissionsTotals,
  fetchUserWorkspaces
} from '../api/queries';
import { IconLink } from './Dashboard';
import { Box, Button, Flex } from '@chakra-ui/react';
import { useKeycloak } from '../components/AuthHOC';
import { ManageWorkspace, WorkspaceNavLoader } from '../components/WorkspaceNav/ManageWorkspace';
import NavbarDashboard from '../components/NavbarDashboard';
import { useAssessmentTotal } from '../pages/MyTask/Assessments/hooks';
import { useAdjudicationsTotal } from '../pages/MyTask/Adjudications/hooks';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars } from '@fortawesome/pro-regular-svg-icons';
import { Menu, MenuButton, MenuItem, MenuList } from '@morressier/alembic';
import { USER_ACTIVITY_TOTALS_QUERY_KEY } from '../constants';

const SubmenuHeader: React.FC<{}> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    currentUser: { id },
    authRealm
  } = useKeycloak();

  const { data, isLoading } = useQuery({
    queryKey: ['myWorkspaces', id],
    queryFn: () => fetchUserWorkspaces(),
    enabled: !!id
  });

  const { data: totalCount, isLoading: isLoadingTotalCount } = useQuery({
    queryKey: [USER_ACTIVITY_TOTALS_QUERY_KEY],
    queryFn: () =>
      fetchUserSubmissionsTotals({ authRealm: authRealm ?? '', userId: id })
  });

  const { data: assessmentListTotal } = useAssessmentTotal();
  const { data: adjudicationListTotal } = useAdjudicationsTotal();
  const workspaces = data?.collection ?? [];
  const hasSubmissions =
    !!totalCount &&
    (totalCount.totalDraftSubmissions > 0 || totalCount.totalSubmissions > 0);
  const hasReviews =
    !!totalCount &&
    (totalCount.totalActiveReviews > 0 || totalCount.totalCompletedReviews > 0);
  const hasAdjudications =
    !!adjudicationListTotal &&
    (adjudicationListTotal.activeTotal > 0 ||
      adjudicationListTotal.completedTotal > 0);
  const hasAssessments =
    !!assessmentListTotal &&
    (assessmentListTotal.activeTotal > 0 ||
      assessmentListTotal.completedTotal > 0);

  const mobileMenuItems = [
    { label: 'Home', href: 'home' },
    ...workspaces.flatMap(workspace => [
      {
        label: `Workflow`,
        href: `workspace/${workspace.shortname}/workflow`
      },
      {
        label: `All Submissions`,
        href: `workspace/${workspace.shortname}/submissions`,
        testId: `${workspace.name}-submissions-page`
      },
      {
        label: `Archive`,
        href: `workspace/${workspace.shortname}/archive?activeTab=all`
      },
      {
        label: `Journals`,
        href: `workspace/${workspace.shortname}/journals`
      },
      {
        label: `Users`,
        href: `workspace/${workspace.shortname}/users`,
        testId: `${workspace.name}-users-page`
      }
    ]),
    { label: '', href: '', isDivider: true as const },
    { label: 'Journal Index', href: 'journal-index' },
    ...(hasSubmissions
      ? [
          {
            label: 'My Submissions',
            href: 'my-submissions?submissionType=my-submissions-draft'
          }
        ]
      : []),
    ...(hasReviews
      ? [{ label: 'My Reviews', href: 'reviews?reviewListType=active' }]
      : []),
    ...(hasAdjudications
      ? [
          {
            label: 'Adjudications',
            href: 'adjudications?status=active-adjudications'
          }
        ]
      : []),
    ...(hasAssessments
      ? [
          {
            label: 'Assessments',
            href: 'assessments?status=active-assessments'
          }
        ]
      : [])
  ];

  const activeLabel =
    mobileMenuItems.find(item =>
      location.pathname.includes(item.href.split('?')[0] ?? '')
    )?.label ?? 'Menu';

  return (
    <>
      <NavbarDashboard />

      <Flex
        paddingX={{ base: 3, md: 4 }}
        paddingY={{ base: 2, md: 0 }}
        borderBottomWidth="1px"
        borderColor="neutralPrimaryBorder"
        borderStyle="solid"
        width="100%"
        direction={{ base: 'column', xl: 'row' }}
        gap={{ base: 2, xl: 0 }}
      >
        <Flex
          display={{ base: 'flex', xl: 'none' }}
          width="100%"
          direction="row"
          wrap="wrap"
          gap={2}
          alignItems="center"
        >
          {isLoading || isLoadingTotalCount ? (
            <WorkspaceNavLoader size="normal" />
          ) : (
            <Menu ariaLabel="Mobile navigation menu">
              <MenuButton
                as={Button}
                leftIcon={<FontAwesomeIcon icon={faBars} />}
                width="auto"
                variant="ghost"
                justifyContent="space-between"
              >
                {activeLabel}
              </MenuButton>
              <MenuList maxH="60vh" overflowY="auto">
                {mobileMenuItems.map((item, index) =>
                  'isDivider' in item ? (
                    <Box
                      key={`divider-${index}`}
                      borderTopWidth="1px"
                      borderColor="neutralPrimaryBorder"
                      my={1}
                    />
                  ) : (
                    <MenuItem
                      key={`${item.label}-${item.href}`}
                      onClick={() => navigate(item.href)}
                      ariaLabel={item.label}
                    >
                      {item.label}
                    </MenuItem>
                  )
                )}
              </MenuList>
            </Menu>
          )}

          {!isLoading && workspaces.length > 0 && (
            <Flex wrap="wrap" gap={2}>
              {workspaces.map(workspace => (
                <ManageWorkspace
                  key={`manage-${workspace.shortname}`}
                  workspace={workspace}
                />
              ))}
            </Flex>
          )}
        </Flex>

        <Flex
          display={{ base: 'none', xl: 'flex' }}
          alignItems="center"
          width="100%"
          justifyContent="flex-start"
          flexDirection="row"
          gap={0}
        >
          <Flex alignItems="center">
            <IconLink href="home">Home</IconLink>
            {isLoading ? (
              <WorkspaceNavLoader size="normal" />
            ) : workspaces.length > 0 ? (
              <Flex alignItems="center" flexWrap="wrap">
                {workspaces.map(workspace => (
                  <Flex key={workspace.shortname} flexWrap="wrap">
                    <IconLink
                      href={`workspace/${workspace.shortname}/workflow`}
                    >
                      Workflow
                    </IconLink>

                    <IconLink
                      data-testId={`${workspace.name}-submissions-page`}
                      href={`workspace/${workspace.shortname}/submissions`}
                    >
                      All Submissions
                    </IconLink>

                    <IconLink href={`workspace/${workspace.shortname}/archive?activeTab=all`}>
                      Archive
                    </IconLink>

                    <IconLink
                      href={`workspace/${workspace.shortname}/journals`}
                    >
                      Journals
                    </IconLink>

                    <IconLink
                      data-testId={`${workspace.name}-users-page`}
                      href={`workspace/${workspace.shortname}/users`}
                    >
                      Users
                    </IconLink>

                    <ManageWorkspace
                      workspace={workspace}
                    />
                  </Flex>
                ))}
              </Flex>
            ) : null}
          </Flex>

          <Flex
            flexWrap="wrap"
            alignItems="center"
            flexShrink={0}
            marginLeft={workspaces.length > 0 ? 'auto' : 0}
          >
            {isLoadingTotalCount ? (
              <WorkspaceNavLoader size="normal" />
            ) : (
              <>
                <IconLink href="journal-index">Journal Index</IconLink>

                {hasSubmissions && (
                  <IconLink href="my-submissions?submissionType=my-submissions-draft">
                    My Submissions
                  </IconLink>
                )}

                {hasReviews && (
                  <IconLink href="reviews?reviewListType=active">
                    My Reviews
                  </IconLink>
                )}
              </>
            )}

            {hasAdjudications && (
              <IconLink href="adjudications?status=active-adjudications">
                Adjudications
              </IconLink>
            )}
            {hasAssessments && (
              <IconLink href="assessments?status=active-assessments">
                Assessments
              </IconLink>
            )}
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};

export default SubmenuHeader;
